import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, type RouteObject } from 'react-router';
import { AppFrame, ConsoleNav, Loading } from '../components.js';
import { I18nProvider } from '../i18n.js';
import { AccountPage, SecurityPage } from '../pages/AccountPages.js';
import { MfaVerifyPage } from '../pages/MfaPages.js';
import { SignInPage } from '../pages/SignInPage.js';
import { SessionProvider } from '../session.js';
import { SurfaceContext } from '../surface.js';
import {
  OperatorPlanDetailPage,
  OperatorPlanNewPage,
  OperatorPlansPage,
  OperatorSettingsPage,
  OperatorStorePage,
  OperatorStoresPage,
} from './OperatorPages.js';
import { LandingContentPage } from './LandingPage.js';
import { OperatorFeedbackPage } from './OperatorFeedbackPage.js';
import { OperatorMerchantPage, OperatorMerchantsPage } from './OperatorMerchantsPage.js';
import { OperatorWatchPage } from './OperatorWatchPage.js';

// Enrollment pulls in the QR code encoder: loaded only when needed.
const MfaSetupPage = lazy(async () => ({
  default: (await import('../pages/MfaSetupPage.js')).MfaSetupPage,
}));

/**
 * The platform operator console (ADR 0025), served on its own origin (console.mrigl.tn): its
 * own sign-in and session, the stores and their subscriptions, plans and billing settings.
 * None of it is in the merchant admin bundle.
 */
export const consoleRoutes: RouteObject[] = [
  { path: '/sign-in', element: <SignInPage /> },
  { path: '/mfa', element: <MfaVerifyPage /> },
  {
    element: (
      <AppFrame>
        <ConsoleNav />
      </AppFrame>
    ),
    children: [
      { index: true, element: <OperatorStoresPage /> },
      { path: 'stores/:storeId', element: <OperatorStorePage /> },
      { path: 'plans', element: <OperatorPlansPage /> },
      { path: 'plans/new', element: <OperatorPlanNewPage /> },
      { path: 'plans/:planId', element: <OperatorPlanDetailPage /> },
      { path: 'settings', element: <OperatorSettingsPage /> },
      { path: 'landing', element: <LandingContentPage /> },
      { path: 'feedback', element: <OperatorFeedbackPage /> },
      { path: 'listings', element: <OperatorWatchPage /> },
      { path: 'merchants', element: <OperatorMerchantsPage /> },
      { path: 'merchants/:merchantId', element: <OperatorMerchantPage /> },
      { path: 'account', element: <AccountPage /> },
      { path: 'security', element: <SecurityPage /> },
      {
        path: 'security/mfa',
        element: (
          <Suspense fallback={<Loading />}>
            <MfaSetupPage />
          </Suspense>
        ),
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
];

export function ConsoleApp() {
  const router = createBrowserRouter(consoleRoutes);
  return (
    <SurfaceContext.Provider value="console">
      <I18nProvider>
        <SessionProvider>
          <RouterProvider router={router} />
        </SessionProvider>
      </I18nProvider>
    </SurfaceContext.Provider>
  );
}
